import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    getEmailSettingsByTokenType,
    getEmailSettingsType,
} from "./__graphql-types__/index.ts";
import type {SubscriptionTarget} from "./types.ts";

interface EmailSettingsQueryResult {
    target: SubscriptionTarget | null | undefined;
    isLoading: boolean;
    isError: boolean;
}

export const mapDataToResultWithToken = ({
    data,
    isError,
    loading,
}: {
    data: getEmailSettingsByTokenType["response"] | null | undefined;
    isError: boolean;
    loading: boolean;
}): EmailSettingsQueryResult => {
    if (loading || !data || !data.emailSettingsFromUnsubscribeToken) {
        return {
            isError,
            isLoading: loading,
            target: null,
        };
    }

    const {email, emailSubscriptions, ...restOfSettings} =
        data.emailSettingsFromUnsubscribeToken;

    return {
        isError,
        isLoading: loading,
        target: {
            from: "token",
            // Don't show all email addresses if user is not logged in
            email,
            authEmails: [email],
            ...emailSubscriptions,
            ...restOfSettings,
        },
    };
};

export const mapDataToResultWithKaid = ({
    data,
    isError,
    loading,
}: {
    data: getEmailSettingsType["response"] | null | undefined;
    isError: boolean;
    loading: boolean;
}): EmailSettingsQueryResult => {
    if (loading || !data || !data.target) {
        return {
            isError,
            isLoading: loading,
            target: null,
        };
    }

    const {id, emailSubscriptions, authEmails, ...restOfSettings} = data.target;

    return {
        isError,
        isLoading: loading,
        target: {
            from: "kaid",
            // Don't show all email addresses if user is not logged in
            authEmails:
                data.actor && data.actor.id === id
                    ? (authEmails ?? [])
                    : [data.target.email],
            ...emailSubscriptions,
            ...restOfSettings,
        },
    };
};

// NOTE: sailthruSubscriptions is queried for in newsletters-section.jsx, as it
// is a longer request because it connects to Sailthru and also because it raises

const emailSubscriptionsFragment = gql`
    fragment EmailSubscriptionFields on EmailSubscriptions {
        allEmailSubscription {
            checked
            key
        }
        answerNotificationEmailSubscription {
            checked
            key
        }
        newAssignmentNotificationEmailSubscription {
            checked
            key
        }
        allStudentsCoachReportEmailSubscription {
            key
            checked
        }
        parentReportEmailSubscriptions {
            key
            description
            checked
        }
        studentListEmailSubscriptions {
            key
            checked
            description
        }
        streaksEmailSubscription {
            token
            checked
        }
        __typename
    }
`;

export const EMAIL_SETTINGS_BY_TOKEN: GqlOperation<
    getEmailSettingsByTokenType["response"],
    getEmailSettingsByTokenType["variables"]
> = gqlOp(gql`
    query getEmailSettingsByToken($token: String!) {
        emailSettingsFromUnsubscribeToken(token: $token) {
            isChild
            isEmailVerified
            hasSendableEmail
            email
            hasStudents
            hasChildren
            emailSubscriptions {
                ...EmailSubscriptionFields
            }
            hasClasses
        }
    }

    ${emailSubscriptionsFragment}
`);

export const EMAIL_SETTINGS_BY_KAID: GqlOperation<
    getEmailSettingsType["response"],
    getEmailSettingsType["variables"]
> = gqlOp(gql`
    query getEmailSettings($targetKaid: String) {
        actor: user {
            id
        }
        target: user(kaid: $targetKaid) {
            id
            isChild
            isEmailVerified
            hasSendableEmail
            email
            authEmails
            hasStudents: hasCoachees
            hasChildren
            pendingEmailVerifications {
                email
            }
            signupDataIfUnverified {
                email
            }
            emailSubscriptions {
                ...EmailSubscriptionFields
            }
            hasClasses
        }
    }

    ${emailSubscriptionsFragment}
`);
